import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import { AttributeContainer, CrossContainer, HeaderContainer, InputsContainer, SubtitleContainer } from './styles';
import { CircularProgress, Divider, FormControl, FormControlLabel, FormHelperText, InputAdornment, InputLabel, MenuItem, Radio, RadioGroup, Select, SelectChangeEvent, TextField } from '@mui/material';
import theme from '../../theme/theme';
import { AddCircle, Close, Edit } from '@mui/icons-material';
import { getLocations } from '../../services/locations/getLocations';
import { getResourceTypes } from '../../services/resourceTypes/getResourceTypes';
import { UploadFile } from '../../components/UploadFile';
import { NumericFormat, NumericFormatProps } from 'react-number-format';
import { CustomText } from '../../components/CustomText';
import { postResource } from '../../services/resources/postResources';
import { RequestModal } from '../../components/RequestModal';
import { ResourceActionsProps } from './types';
import { putResources } from '../../services/resources/putResources';

const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '70%',
    minWidth: '380px',
    maxHeight: '80%',
    overflow: 'auto',
    borderRadius: '20px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
};

const ResourceActions: React.FC<ResourceActionsProps> = ({ action, resourceObj, onResponse }) => {
    // ------------------- JWT -------------------
    const jwt = localStorage.getItem('jwt');
    const resourceId = resourceObj?.id;

    const hardware_variations = ["Hardware", "hardware", "HARDWARE", "Equipo", "equipo", "EQUIPO", "Equipo de computo", "equipo de computo", "EQUIPO DE COMPUTO",
    "Equipo de cómputo", "equipo de cómputo", "EQUIPO DE CÓMPUTO", "Equipo de Cómputo", "equipo de Cómputo", "EQUIPO DE CÓMPUTO",
    "Equipo de computación", "equipo de computación", "EQUIPO DE COMPUTACIÓN", "Equipo de Computación", "equipo de Computación", "EQUIPO DE COMPUTACIÓN",
    "Equipos de computo", "equipos de computo", "EQUIPOS DE COMPUTO", "Equipos de cómputo", "equipos de cómputo", "EQUIPOS DE CÓMPUTO",
    "equipos", "Equipos", "EQUIPOS", "Equipo de computo", "equipo de computo", "EQUIPO DE COMPUTO", "Equipo de cómputo", "equipo de cómputo", "EQUIPO DE CÓMPUTO"]
    
    const software_variations = ["Software", "software", "SOFTWARE", "Programa", "programa", "PROGRAMA", "Programas", "programas", "PROGRAMAS", "Aplicación", "aplicación", "APLICACIÓN",
    "Licencias", "licencias", "LICENCIAS", "Licencia", "licencia", "LICENCIA", "Sistema operativo", "sistema operativo", "SISTEMA OPERATIVO", "Sistema Operativo", "sistema Operativo", "SISTEMA OPERATIVO"]

    const imageSpecs = `
    Esta sección es para subir las imágenes correspondientes al recurso. Considere lo siguiente: 
    • Puede [arrastrar y soltar] las imágenes o hacer [click] en el botón para seleccionarlas.
    
    • El límite son [100 MB] en total. 
    
    • Las imágenes deben ser [horizontales]. 
    
    • El formato de las imágenes debe ser [.jpg] o [.png]. 
    
    • Se aceptan hasta [5 imágenes].

    • Si quiere editar las fotografías del recurso, debe subir [todas las deseadas] nuevamente.

    • Si no quiere editar las imágenes del recurso, [omitir esta sección].
    `;

    // ------ Modal ------
    const [open, setOpen] = React.useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setVerified(false);
        setUploadFile(null);
        setIsPosted(false);
        setInvalid(false);
        if (action != 'edit') {
            setResourceName('');
            setLocation('');
            setType('');
            setCategory('');
            setMaxTime('');
            setTimeUnits('');
            setDescription('');
        }
    };

    // ------ States ------
    const [places, setPlaces] = React.useState<any>([]);
    const [resourceTypes, setResourceTypes] = React.useState<any>([]);

    const [resourceName, setResourceName] = React.useState(action === 'create' ? '' : resourceObj.name);
    const [location, setLocation] = React.useState(action === 'create' ? '' : resourceObj.location[0]);
    const [type, setType] = React.useState(action === 'create' ? '' : resourceObj.resourceObj_type);
    const [category, setCategory] = React.useState(action === 'create' ? '' : resourceObj.category);
    const [maxTime, setMaxTime] = React.useState(action === 'create' ? '' : resourceObj.max_reservation_time);
    const [timeUnits, setTimeUnits] = React.useState(action === 'create' ? '' : resourceObj.max_reservation_measure);
    const [description, setDescription] = React.useState(action === 'create' ? '' : resourceObj.description);
    const [resourceList, setResourceList] = React.useState([""]);
    const [activeStock, setActiveStock] = React.useState(false);
    const [stock, setStock] = React.useState(resourceObj ? resourceObj?.stock : 0);

    const [verified, setVerified] = React.useState(false);
    const [uploadFile, setUploadFile] = React.useState<any>(null);

    const [isPosted, setIsPosted] = React.useState(false);
    const [invalid, setInvalid] = React.useState(false);
    const [warningTitle, setWarningTitle] = React.useState("");
    const [warningMessage, setWarningMessage] = React.useState("");

    const [loadingData, setLoadingData] = React.useState(false);
    const [loadingPostResource, setLoadingPostResource] = React.useState(false);

    const [resourceNameError, setResourceNameError] = React.useState(false);
    const [locationError, setLocationError] = React.useState(false);
    const [typeError, setTypeError] = React.useState(false);
    const [categoryError, setCategoryError] = React.useState(false);
    const [maxTimeError, setMaxTimeError] = React.useState(false);
    const [descriptionError, setDescriptionError] = React.useState(false);

    // ------ Handlers ------
    const handleChangeType = (event: SelectChangeEvent) => {
        setType(event.target.value);
        resourceTypes.find((type: any) => {
            if (type.id === event.target.value) {
                setCategory(type.category);
            }
        })
    };

    // ------ Requests ------
    const handleResource = async () => {
        const resource = new FormData();
        resource.append('name', resourceName);
        resource.append('resource_type', type);
        resource.append('description', description);
        if (hardware_variations.includes(category) || software_variations.includes(category)) {
            resource.append('stock', stock.toString());
            for (let i = 0; i < stock; i++) {
                resource.append('resource_list', i.toString());
            }
        }
        resource.append('location', location);
        resource.append('active', 'true');
        if (uploadFile) {
            resource.append('img_length', uploadFile.length);
        } else{
            resource.append('img_length', '0');
        }
        resource.append('max_reservation_time', maxTime);
        resource.append('max_reservation_measure', timeUnits);
        resource.append('only_on_site', 'false');
        if (action === 'create' || uploadFile) {
            for (let i = 0; i < uploadFile.length; i++) {
                resource.append('image_' + (i + 1), uploadFile[i]);
            }
        }

        // resource.forEach((value, key) => {
        //     if (value instanceof File) {
        //         console.log(`${key}:`);
        //         console.log(`  Name: ${value.name}`);
        //         console.log(`  Size: ${value.size} bytes`);
        //         console.log(`  Type: ${value.type}`);
        //         console.log(`  Last Modified: ${value.lastModified}`);
        //     } else {
        //         console.log(`${key}: ${value}`);
        //     }
        // });

        if (!resourceName || !location || !type || !maxTime || !timeUnits || !description) {
            setWarningTitle("Campos incompletos");
            setWarningMessage("Debe completar todos los campos. Por favor, inténtelo de nuevo.");
            setInvalid(true);
        } else {
            if (action === 'create') {
                setLoadingPostResource(true);
                await postResource(jwt!, resource)
                    .then((res) => {
                        if (res.status === 201) {
                            setWarningTitle("¡Recurso creado!");
                            setWarningMessage("El recurso se creó correctamente.");
                            setIsPosted(true);
                            onResponse && onResponse();
                        } else {
                            setWarningTitle("Ocurrió un error");
                            setWarningMessage("Ocurrió un error al crear el recurso. Inténtelo de nuevo.");
                            setInvalid(true);
                        }
                    })
                    .catch((err) => {
                        console.log(err);
                    })
                setLoadingPostResource(false);
            } else if (action === 'edit') {
                setLoadingPostResource(true);
                await putResources(jwt!, resourceId, resource)
                    .then((res) => {
                        if (res.status === 201) {
                            setWarningTitle("¡Recurso editado!");
                            setWarningMessage("El recurso se editó correctamente.");
                            setIsPosted(true);
                            onResponse && onResponse();
                        } else {
                            setWarningTitle("Ocurrió un error");
                            setWarningMessage("Ocurrió un error al editar el recurso. Inténtelo de nuevo.");
                            setInvalid(true);
                        }
                    })
                    .catch((err) => {
                        console.log(err);
                    })
                setLoadingPostResource(false);
            }
        }
    }

    const handleStockChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let tempStock = event.target.value === "" || parseInt(event.target.value) < 0 ? 0 : parseInt(event.target.value);
        setStock(tempStock);
        setResourceList([]);
    }

    const handleResourceName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setResourceName(event.target.value);
    };

    const handleResourceNameBlur = () => {
        if (resourceName.trim() === '') {
            setResourceNameError(true);
        } else {
            setResourceNameError(false);
        }
    };

    const handleLocation = (event: SelectChangeEvent<any>) => {
        setLocation(event.target.value);
    };

    const handleLocationBlur = () => {
        if (location === '') {
            setLocationError(true);
        } else {
            setLocationError(false);
        }
    };

    const handleTypeData = (event: React.ChangeEvent<HTMLInputElement>) => {
        setType(event.target.value);
    };

    const handleTypeBlur = () => {
        if (type === '') {
            setTypeError(true);
        } else {
            setTypeError(false);
        }
    };

    const handleMaxTime = (event: React.ChangeEvent<HTMLInputElement>) => {
        let tempTime = event.target.value === "" || parseInt(event.target.value) < 0 ? 0 : parseInt(event.target.value);
        setMaxTime(tempTime);
    };

    const handleMaxTimeBlur = () => {
        if (parseInt(maxTime) < 0) {
            setMaxTimeError(true);
        } else {
            setMaxTimeError(false);
        }
    };

    const handleDescription = (event: React.ChangeEvent<HTMLInputElement>) => {
        setDescription(event.target.value);
    };

    const handleDescriptionBlur = () => {
        if (description.trim() === '') {
            setDescriptionError(true);
        } else {
            setDescriptionError(false);
        }
    };

    const changeTimeUnits = (event: SelectChangeEvent) => {
        if (event.target.value === "day") {
            setTimeUnits("day");
        } else {
            setTimeUnits("hour");
        }
    };

    // ------ Inputs ------
    interface CustomProps {
        onChange: (event: { target: { name: string; value: string } }) => void;
        name: string;
    }

    const NumericFormatCustom = React.forwardRef<NumericFormatProps, CustomProps>(
        function NumericFormatCustom(props, ref) {
            const { onChange, ...other } = props;

            return (
                <NumericFormat
                    {...other}
                    getInputRef={ref}
                    onValueChange={(values: any) => {
                        onChange({
                            target: {
                                name: props.name,
                                value: values.value,
                            },
                        });
                    }}
                    thousandSeparator
                    valueIsNumericString
                />
            );
        },
    );

    // ------ Render ------
    const getData = async () => {
        await getLocations(jwt!)
            .then((res) => {
                setPlaces(res.data);
            })
            .catch((err) => {
                console.log(err);
            });

        await getResourceTypes(jwt!)
            .then((res) => {
                setResourceTypes(res.data);

                res.data.find((type: any) => {
                    if (type.name === resourceObj.resource_type) {
                        setType(type.id);
                    }
                })
            })
            .catch((err) => {
                console.log(err);
            });
    };


    React.useEffect(() => {
        setLoadingData(true);
        getData();
        setLoadingData(false);
    }, []);

    React.useEffect(() => {
        if (hardware_variations.includes(category) || software_variations.includes(category)) {
            setActiveStock(true);
        } else {
            setActiveStock(false);
        }
    }, [category]);

    return (
        <div>
            {
                isPosted ? (
                    <RequestModal
                        title={warningTitle}
                        description={warningMessage}
                        onConfirm={handleClose}
                    />
                ) : (
                    invalid &&
                    <RequestModal
                        title='Ocurrió un error'
                        description={warningMessage}
                        onConfirm={(!resourceName || !location || !type || !maxTime || !timeUnits || !description) ?
                            () => { setInvalid(false) } : handleClose}
                    />
                )
            }
            <Button onClick={handleOpen} style={{ padding: '0px' }} >
                {action === 'create' ?
                    <AddCircle sx={{ color: theme.color.navy, fontSize: '2rem' }} />
                    :
                    <Edit sx={{ color: theme.color.navy, fontSize: '2rem' }} />
                }
            </Button>
            <Modal
                open={open}
                // onClose={handleClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={style}>
                    {
                        loadingData &&
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <CircularProgress sx={{ color: theme.color.navy }} />
                        </div>
                    }
                    {loadingPostResource ? (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <CircularProgress sx={{ color: theme.color.navy }} />
                        </div>
                    ) : (
                        <>
                            <CrossContainer
                                onClick={handleClose}
                            >
                                <Close />
                            </CrossContainer>
                            <HeaderContainer>
                                {
                                    action === 'create' ?
                                        "Agregar nuevo recurso"
                                        :
                                        "Editar recurso"
                                }
                            </HeaderContainer>
                            <InputsContainer>
                                <AttributeContainer>
                                    <TextField
                                        label="Nombre del recurso"
                                        type="text"
                                        variant="outlined"
                                        fullWidth
                                        required
                                        sx={{ margin: "15px 0", width: '100%' }}
                                        InputProps={{
                                            style: {
                                                borderRadius: "14px",
                                            },
                                        }}
                                        value={resourceName}
                                        onChange={handleResourceName}
                                        onBlur={handleResourceNameBlur}
                                        error={resourceNameError}
                                        helperText={resourceNameError ? 'Este campo es obligatorio' : ''}
                                    />
                                </AttributeContainer>
                                <FormControl sx={{ margin: "15px 0", width: '100%' }}>
                                    <InputLabel id="demo-simple-select-autowidth-label" >Ubicación</InputLabel>
                                    <Select
                                        labelId="demo-simple-select-autowidth-label"
                                        id="demo-simple-select-autowidth"
                                        autoWidth
                                        label="Ubicación"
                                        sx={{ borderRadius: '15px' }}
                                        value={location}
                                        onChange={handleLocation}
                                        onBlur={handleLocationBlur}
                                        error={locationError}
                                    // helperText={resourceNameError ? 'Este campo es obligatorio' : ''}
                                    >
                                        {places && places
                                            .filter((place: any) => !place.deleted && place.id !== 1000)
                                            .map((place: any) => (
                                                <MenuItem value={place.id}>{place.name}</MenuItem>
                                            ))
                                        }
                                    </Select>
                                    {locationError && <FormHelperText sx={{ color: '#C61A24' }}>Este campo es obligatorio</FormHelperText>}
                                </FormControl>
                            </InputsContainer>
                            <InputsContainer>
                                <FormControl sx={{ margin: "15px 0", width: '100%' }}>
                                    <InputLabel id="demo-simple-select-autowidth-label">Tipo</InputLabel>
                                    <Select
                                        labelId="demo-simple-select-autowidth-label"
                                        id="demo-simple-select-autowidth"
                                        autoWidth
                                        sx={{ borderRadius: '15px' }}
                                        label="Tipo"
                                        value={type}
                                        onChange={handleChangeType}
                                        onBlur={handleTypeBlur}
                                        error={typeError}
                                    >
                                        {resourceTypes.map((type: any) => (
                                            <MenuItem value={type.id}>{type.name}</MenuItem>
                                        ))}
                                    </Select>
                                    {typeError && <FormHelperText sx={{ color: '#C61A24' }}>Este campo es obligatorio</FormHelperText>}
                                </FormControl>
                                <AttributeContainer>
                                    <TextField
                                        label="Categoría"
                                        type="text"
                                        variant="outlined"
                                        value={category}
                                        fullWidth
                                        disabled={true}
                                        sx={{ margin: "15px 0", width: '100%' }}
                                        InputProps={{
                                            style: {
                                                borderRadius: "14px",
                                            },
                                        }}
                                    />
                                </AttributeContainer>
                                <AttributeContainer>
                                    <TextField
                                        label="Tiempo máximo"
                                        type="number"
                                        variant="outlined"
                                        fullWidth
                                        required
                                        sx={{ margin: "15px 0", width: '100%' }}
                                        InputProps={{
                                            // inputComponent: NumericFormatCustom as any,
                                            endAdornment: <InputAdornment position="end">{timeUnits}</InputAdornment>,
                                            style: {
                                                borderRadius: "14px",
                                            },
                                        }}
                                        value={maxTime}
                                        onChange={handleMaxTime}
                                        onBlur={handleMaxTimeBlur}
                                        error={maxTimeError}
                                        helperText={maxTimeError ? 'Este campo es obligatorio' : ''}
                                    />
                                </AttributeContainer>
                                <AttributeContainer>
                                    <FormControl>
                                        {/* <FormLabel id="time-units">Tiempo máximo de reserva</FormLabel> */}
                                        <RadioGroup
                                            aria-label="status"
                                            name="status_radio_group"
                                            sx={{ flexDirection: "row" }}
                                            value={timeUnits}
                                            onChange={changeTimeUnits}
                                        >
                                            <FormControlLabel value="hour" control={<Radio />} label="horas" />
                                            <FormControlLabel value="day" control={<Radio />} label="días" />
                                        </RadioGroup>
                                    </FormControl>
                                </AttributeContainer>
                            </InputsContainer>
                            <AttributeContainer>
                                <TextField
                                    label="Descripción"
                                    type="text"
                                    variant="outlined"
                                    maxRows={4}
                                    multiline
                                    fullWidth
                                    required
                                    sx={{ margin: "15px 0", width: '100%' }}
                                    InputProps={{
                                        style: {
                                            borderRadius: "14px",
                                        },
                                    }}
                                    value={description}
                                    onChange={handleDescription}
                                    onBlur={handleDescriptionBlur}
                                    error={descriptionError}
                                    helperText={descriptionError ? 'Este campo es obligatorio' : ''}
                                />
                            </AttributeContainer>
                            {
                                activeStock ?
                                    <AttributeContainer>
                                        <TextField
                                            label="Cant. en stock"
                                            required
                                            type="number"
                                            inputProps={{ maxLength: 1 }}
                                            sx={{ width: "100%" }}
                                            InputProps={{
                                                style: {
                                                    borderRadius: "14px",
                                                },
                                            }}
                                            value={stock}
                                            onChange={handleStockChange}
                                        // onChange={handleDoors}
                                        // value={doors}
                                        // onBlur={handleDoorsBlur}
                                        // error={doorsError}
                                        // helperText={doorsError ? 'Este campo es obligatorio' : ''}
                                        />
                                        {/* {stock !== 0 && (
                                            <div>
                                                {[...Array(stock)].map((_, index) => (
                                                    <TextField
                                                        key={index}
                                                        label={`Recurso ${index + 1}`}
                                                        name={index.toString()}
                                                        type="text"
                                                        variant="outlined"
                                                        fullWidth
                                                        required
                                                        value={resourceList[index] || ""}
                                                        onChange={(e) => {
                                                            const updatedResourceList = [...resourceList];
                                                            updatedResourceList[index] = e.target.value;
                                                            setResourceList(updatedResourceList);
                                                        }}
                                                        sx={{ margin: "15px 0", width: '100%' }}
                                                        InputProps={{
                                                            style: {
                                                                borderRadius: "14px",
                                                            },
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        )} */}

                                    </AttributeContainer>
                                    : null
                            }
                            <SubtitleContainer>Imágenes</SubtitleContainer>
                            <Divider />
                            <CustomText
                                color={theme.color.navy}
                                content={imageSpecs}
                            />
                            <UploadFile
                                customDescription='Arrastra y suelta 5 imágenes para subirlas.'
                                uploadMany={true}
                                changeFiles={setUploadFile}
                                status={2}
                                verified={verified}
                                setVerified={setVerified}
                            />
                            <Button
                                variant="contained"
                                onClick={handleResource}
                                sx={{
                                    width: "100%",
                                    marginTop: "20px",
                                    borderRadius: "15px",
                                    fontSize: "16px",
                                    backgroundColor: theme.color.navy,
                                    color: theme.color.white,
                                    textTransform: "none",
                                    "&:hover": {
                                        backgroundColor: theme.color.space,
                                    },
                                }}
                            >
                                Guardar
                            </Button>
                        </>
                    )}
                </Box>
            </Modal>
        </div>
    );
}

export default ResourceActions;